<template>
  <div>
    <!--Switch-->
    <section-page
      title="Email Integration: Custom Domain"
      is-short
      no-bottom-margin
      :class="[isOnboardingIntegration ? 'mt-0' : '']"
    >
      <template v-if="!isOnboardingIntegration" v-slot:right>
        <button-link
          class="mr-100"
          size=""
          :loading="isGmailLoading"
          @click="switchToGmail"
        >Switch to Gmail
        </button-link>
      </template>
    </section-page>

    <!--Domain-->
    <section-page
      title="Email Domain"
      description="This is the domain that you will be using for sending and receiving emails inside Forefront. You can still use this domain with another email service like Gmail."
      is-short
    >
      <!--Set Up Domain-->
      <div v-if="showDomainForm" class="flex flex-vertical-center mt-100">
        <p class="section_domain"><strong>Domain Name</strong></p>

        <form-field-input
          v-model="domainNameForm.email_domain"
          placeholder="i.e. example.com"
          class="mb-0 mr-100"
          size=""
          help-text="Do not include the subdomain such as www. when entering domain name"
        ></form-field-input>

        <button-outlined
          @click="saveDomain"
          :loading="saveDomainIsLoading"
          size=""
        >Save Domain
        </button-outlined>

        <button-link
          class="ml-100"
          @click="cancelChangeDomain"
          size=""
        >cancel
        </button-link>
      </div>

      <!--Verify Domain-->
      <template v-else>
        <p class="section_domain">
          <strong>Domain Name: </strong> {{ account.email_domain }}
          <span class="section_domain--link" @click="showDomainForm = true">change domain</span>
        </p>

        <template v-if="domainData">
          <h3 class="section_heading">DNS Records
            <app-help-link page-uri="knowledge-base/forefront-email-set-up/how-to-add-dns-records/">How to add DNS
              Records
            </app-help-link>
          </h3>
          <p class="section_description">Add the following DNS records to your domain so we can send emails on your behalf
            and track things such as open, clicked, etc.</p>

          <!--Select Domain Provider-->
          <form-field-select
            class="domain-provider"
            label="Select Domain Provider"
            v-model="domainProviderSelected"
            @change="updateDomainProvider"
          >
            <option value="null">None</option>
            <option
              v-for="option in ENUMS.DomainServiceProviderTypeEnum"
              :value="option.value"
            >{{ option.label }}</option>
          </form-field-select>

          <table-default
            class="mb-100"
            :table-data="domainData.sending_dns_records"
          >
            <b-table-column label="Type" field="record_type" v-slot="props">
              {{ props.row.record_type }}
            </b-table-column>

            <b-table-column label="Host" field="host" v-slot="props">
              <template v-if="getDomainHostValue(props.row.name)">
                {{ getDomainHostValue(props.row.name) }}
                <span class="table_link" @click="copyText(getDomainHostValue(props.row.name))"><strong>copy</strong></span>
              </template>
              <template v-else>
                <em class="has-text-muted">(leave it blank)</em>
              </template>
            </b-table-column>

            <b-table-column label="Value" field="value" cell-class="max-w-300" v-slot="props">
              {{ props.row.value }} <span
              class="table_link"
              @click="copyText(props.row.value)"
            ><strong>copy</strong></span>
            </b-table-column>

            <b-table-column label="Status" field="valid" numeric v-slot="props">
              <span v-if="props.row.valid === 'valid'" class="has-text-success">verified</span>
              <span v-else class="has-text-danger">unverified</span>
            </b-table-column>
          </table-default>

          <div class="flex flex-between flex-vertical-center">
            <p>NOTE: while changes to DNS records usually take an hour or so it may take up to 24 hours to update.</p>

            <button-outlined
              @click="verifyDomain"
              :loading="verifyDomainIsLoading"
            >Verify DNS
            </button-outlined>
          </div>
        </template>
      </template>
    </section-page>

    <!--Email Address-->
    <section-page
      title="Email Addresses"
      description="Set up an outgoing email addresses for your users and to get an unique email to forward their incoming email addresses to."
      is-short
    >
      <template v-if="isDnsVerified">
        <form-field-radio
          v-model="hasEmailClient"
          :items="emailClientRadioOptions"
          :option-label="(option) => option.label"
          :option-value="(option) => option.id"
          size=""
        ></form-field-radio>

        <!--Email Client Step-->
        <div class="step" v-if="!hasEmailClient">
          <h4 class="step--heading">1. No worries. You'll need to setup an email client first.</h4>

          <p class="step--description">This is necessary in order for emails responses to be received and then forwarded
            to Forefront to be
            tracked. Plus, you and your team should probably have company email addresses. :)</p>

          <p class="step--description mt-200">Instructions for setting up popular email clients</p>

          <ul>
            <li class="step--link">
              <app-help-link page-uri="knowledge-base/forefront-email-set-up/set-up-a-google-workspace-gsuite-email/">Setting up Google Workspace email client</app-help-link>
            </li>
            <li class="step--link">
              <app-help-link page-uri="knowledge-base/forefront-email-set-up/set-up-a-microsoft-office-365-email/">Setting up Office 365 email client</app-help-link>
            </li>
            <li class="step--link">
              <app-help-link page-uri="knowledge-base/forefront-email-set-up/whats-the-best-email-client-host/">See more email clients with instructions on how to
                setup
              </app-help-link>
            </li>
          </ul>
        </div>

        <!--Outgoing Addresses Step-->
        <div class="step">
          <h4 class="step--heading">{{ hasEmailClient ? '1' : '2' }}. Great! Please enter the email addresses you have for each user here</h4>

          <p class="step--description">This will allow your users to send emails from Forefront using these email
            addresses. Emails sent from Forefront will not show in your email client's 'Sent' folder as Forefront is doing the actual sending.</p>

          <table-default
            class="my-100"
            :table-data="users"
          >
            <b-table-column label="User" field="full_name" v-slot="props">
              {{ props.row.full_name }}
            </b-table-column>

            <b-table-column label="Email Address" field="outgoing_email_name" v-slot="props">
              <div v-if="editOutgoingEmailUserId === props.row.id" class="flex flex-vertical-center">
                <form-field-input
                  v-model="editUserDomainForm.outgoing_email_name"
                  class="mb-0 mr-100"
                >
                  <template v-slot:after>
                    <span class="button is-small is-static field-button">@{{ domainData.domain.name }}</span>
                  </template>
                </form-field-input>

                <button-outlined
                  @click="saveOutgoingEmail(props.row)"
                  :loading="saveOutgoingIsLoading"
                  :disabled="saveOutgoingIsLoading"
                >Save Email
                </button-outlined>

                <button-link
                  class="ml-100"
                  @click="editOutgoingEmailUserId = null"
                >cancel
                </button-link>
              </div>

              <template v-else>
                <template v-if="getMailgunIntegrationByUserId(props.row.id)">
                  {{ getMailgunIntegrationByUserId(props.row.id).data.outgoing_email_name }}@{{ account.email_domain }} <span
                  class="table_link"
                  @click="editUserDomain(props.row)"
                >edit</span>
                </template>

                <template v-else>
                  <em>not set</em> <span class="table_link" @click="editUserDomain(props.row)">enter email address</span>
                </template>
              </template>
            </b-table-column>
          </table-default>

          <p class="section_note">
            NOTE: We will include an unsubscribe link in the bottom of every email sent out. This is done to keep our
            email reputation in good standing so that your emails don’t end up in the spam folder or blocked by the
            ESPs.
            <app-help-link page-uri="knowledge-base/forefront-email-set-up/learn-how-to-not-end-up-in-spam-folders/">
              Learn
              how to not end up in spam folders
            </app-help-link>
          </p>
        </div>

        <!--Incoming Addresses Step-->
        <div class="step">
          <h4 class="step--heading">{{ hasEmailClient ? '2' : '3' }}. Now you'll need to setup an email forward for each user in your existing email client.</h4>

          <p class="step--description">This will allow Forefront to receive a forwarded copy of emails sent to your
            users' emails. This allows
            tracking of emails received from contacts within Forefront. Emails received from email addresses not found
            belonging to any contacts in your Forefront account will be ignored. This eliminates spam.</p>

          <p class="step--directions">Copy each forwarding email address and set as an email forwarder in your existing
            email client.
            <app-help-link page-uri="knowledge-base/settings/emailsettings/forefront-email-connecting-gsuite-google-workspace-emails/">
              How to set up email forwarding
            </app-help-link>
          </p>

          <table-default
            class="my-100"
            :table-data="users"
          >
            <b-table-column label="User / Email Address" field="full_name" v-slot="props">
              {{ props.row.full_name }}
              <template v-if="getMailgunIntegrationByUserId(props.row.id)">
                ({{ getMailgunIntegrationByUserId(props.row.id).data.outgoing_email_name }}@{{ account.email_domain }})
              </template>

              <template v-else>
                <em>(not set)</em>
              </template>
            </b-table-column>

            <b-table-column label="Email Address to Forward To" field="forwarding_email_name" v-slot="props">
              <template v-if="getMailgunIntegrationByUserId(props.row.id)">
                {{ props.row.forwarding_email_name }}@{{ forwardingDomain }}
                <span
                  class="table_link"
                  @click="copyText(props.row.forwarding_email_name + '@' + forwardingDomain)"
                ><strong>copy</strong></span>
              </template>

              <template v-else>
                <span class="has-text-muted"><em>please create an outgoing email above first</em></span>
              </template>
            </b-table-column>

            <b-table-column cell-class="action_cell" v-slot="props">
              <div class="flex flex-between" v-if="getMailgunIntegrationByUserId(props.row.id)">
                <!-- Forward setup and waiting for verification -->
                <template v-if="userIsUnverified(props.row)">
                  <template v-if="getMailgunConfirmationCode(props.row.id)">Confirmation Code: {{getMailgunConfirmationCode(props.row.id)}}</template>
                  <template v-else-if="getMailgunConfirmationLink(props.row.id)">Confirmation Link: <a target="_blank" :href="getMailgunConfirmationLink(props.row.id)">click here</a></template>
                </template>

                <button-outlined
                  :loading="editUserDomainForm.verifyButtonLoading"
                  @click="verifyEmail(props.row)"
                >
                  Verify
                </button-outlined>

                <div class="pt-2 pl-2 text">
                  <span v-if="userIsUnverified(props.row)" class="has-text-danger">unverified</span>
                  <span v-else-if="userIsVerifying(props.row)" class="has-text-info">verifying</span>
                  <span v-else-if="userIsVerified(props.row)" class="has-text-success">verified</span>
                  <span v-else class="has-text-danger">failed</span>
                </div>
              </div>
            </b-table-column>
          </table-default>
        </div>
      </template>

      <p v-else class="my-200 is-size-5 is-italic has-text-muted">You must add your domain name and verify DNS before
        setting up email address</p>
    </section-page>
  </div>
</template>

<script>
import FormValidationErrors from "../../../components/form/FormValidationErrors.vue";
import {ENUMS} from "../../../services/enums";

export default {
  name: "IntegrationCustomDomain",
  components: {FormValidationErrors},
  props: {
    isOnboardingIntegration: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      forwardingDomain: process.env.MIX_MAIL_GUN_INCOMING_EMAIL_KEY,
      showDomainForm: false,
      domainNameForm: {
        email_domain: "",
      },
      editUserDomainForm: {
        user_id: null,
        integration_id: null,
        outgoing_email_name: '',

        // Ui only State
        verifyButtonLoading: false,
      },
      saveDomainIsLoading: false,
      verifyDomainIsLoading: false,
      editOutgoingEmailUserId: null,
      saveOutgoingIsLoading: false,
      isGmailLoading: false,
      hasEmailClient: true,
      domainProviderSelected: null,
      verifyEmailLoading: []
    };
  },
  computed: {
    account() {
      return this.$store.getters.account;
    },
    domainData() {
      return this.$store.getters.mailgunDomainData;
    },
    isDomainVerified() {
      return !!this.domainData;
    },
    isDnsVerified() {
      return this.$store.getters.mailgunDnsIsVerified;
    },
    mailgunIntegrations() {
      return this.$store.getters.mailgunIntegrations
    },
    users() {
      return this.$store.getters.activeUsers;
    },
    emailClientRadioOptions() {
      return [
        {
          id: true,
          label: "I currently have email addresses setup for " + this.account.email_domain + " in an email client like Google Workspace, Office365, or other."
        },
        {
          id: false,
          label: "I do not have email addresses setup for " + this.account.email_domain
        }
      ];
    },
    domainProviderHostValues() {
      return [
        {
          type: ENUMS.DomainServiceProviderTypeEnum.GoDaddy.value,
          config: {
            domainSymbol: '@',
            includeDomain: false
          }
        },
        {
          type: ENUMS.DomainServiceProviderTypeEnum.DreamHost.value,
          config: {
            domainSymbol: '',
            includeDomain: false
          }
        },
        {
          type: ENUMS.DomainServiceProviderTypeEnum.Wix.value,
          config: {
            domainSymbol: '',
            includeDomain: false
          }
        },
        {
          type: ENUMS.DomainServiceProviderTypeEnum.Namecheap.value,
          config: {
            domainSymbol: '@',
            includeDomain: false
          }
        },
        {
          type: ENUMS.DomainServiceProviderTypeEnum.NameCom.value,
          config: {
            domainSymbol: '',
            includeDomain: false
          }
        },
        {
          type: ENUMS.DomainServiceProviderTypeEnum.Cloudflare.value,
          config: {
            domainSymbol: '@',
            includeDomain: false
          }
        },
        {
          type: ENUMS.DomainServiceProviderTypeEnum.Siteground.value,
          config: {
            domainSymbol: '',
            includeDomain: false
          }
        },
      ];
    },
  },
  mounted() {
    this.domainNameForm.email_domain = this.account.email_domain;
    this.domainProviderSelected = this.account.domain_provider;
    if (!this.account.email_domain) {
      this.showDomainForm = true;
    }
  },
  methods: {
    getMailgunIntegrationByUserId(userId) {
      return this.mailgunIntegrations.find(i => i.user_id === userId);
    },
    getMailgunConfirmationCode(userId) {
      return this.getMailgunIntegrationByUserId(userId).data.google_forwarding_verification_code
    },
    getMailgunConfirmationLink(userId) {
      return this.getMailgunIntegrationByUserId(userId).data.google_forwarding_verification_link
    },
    userIsUnverified(user) {
      let integration = this.getMailgunIntegrationByUserId(user.id);
      if (integration && (integration.data.offset_verifying_at !== null && integration.data.verified_at === null)) {
        return (integration.data.google_forwarding_verification_link || integration.data.google_forwarding_verification_code)
      }

      return false
    },
    userIsVerified(user) {
      let integration = this.getMailgunIntegrationByUserId(user.id);
      if (integration && ((integration.data.offset_verifying_at || integration.data.verified_at))) {
        return integration.data.verified_at;
      }

      return false;
    },
    userIsVerifying(user) {
      let integration = this.getMailgunIntegrationByUserId(user.id);
      if (integration && ((integration.data.offset_verifying_at || integration.data.verified_at))) {
        const now = new Date().getTime();
        const offsetVerifyingAt = new Date(new Date(integration.data.offset_verifying_at).toISOString()).getTime();

        return (!integration.data.verified_at && now < offsetVerifyingAt);
      }

      return false;
    },
    userFailedVerification(user) {
      let integration = this.getMailgunIntegrationByUserId(user.id);
      if (integration && ((integration.data.offset_verifying_at || integration.data.verified_at))) {
        const now = new Date().getTime();
        const offsetVerifyingAt = new Date(new Date(integration.data.offset_verifying_at).toISOString()).getTime();

        return (!integration.data.verified_at && now > offsetVerifyingAt);
      }

      return false;
    },
    switchToGmail() {
      const form = {
        market_id: this.$store.getters.market.id,
        email_integration_type: ENUMS.EmailIntegrationTypeEnum.Gmail.value
      };

      this.$utils.confirm(
        "Are you sure you want to switch? Custom Domain settings will be deleted and will not be recoverable.",
        "Switch to Gmail"
      ).then(() => {
        this.isGmailLoading = true;
        this.$api.markets.setEmailIntegration(form)
          .finally(() => {
            this.isGmailLoading = false;
          });
      });
    },
    saveDomain() {
      this.saveDomainIsLoading = true;

      return this.$api.accounts.changeEmailDomain(this.domainNameForm)
        .then(res => {
          this.$buefy.toast.open({
            message: "Domain name added",
            type: "is-success"
          });

          this.showDomainForm = false;

          return res;
        })
        .catch(error => {

        })
        .finally(() => {
          this.saveDomainIsLoading = false;
        });
    },
    verifyDomain() {
      this.verifyDomainIsLoading = true;

      return this.$api.accounts.verifyDomain()
        .then(res => {
          this.$buefy.toast.open({
            message: "Verifying DNS successful",
            type: "is-success"
          });

          return res;
        })
        .finally(() => {
          this.verifyDomainIsLoading = false;
        });
    },
    cancelChangeDomain() {
      this.showDomainForm = false;
      this.editUserDomainForm.user_id = null;
      this.editUserDomainForm.integration_id = null;
      this.editUserDomainForm.outgoing_email_name = '';
    },
    editUserDomain(user) {
      let integration = this.getMailgunIntegrationByUserId(user.id);
      this.editOutgoingEmailUserId = user.id;
      this.editUserDomainForm.user_id = user.id;
      this.editUserDomainForm.integration_id = (integration) ? integration.id : null;
      this.editUserDomainForm.outgoing_email_name = (integration) ? integration.data.outgoing_email_name : '';
    },
    saveOutgoingEmail(user) {
      this.saveOutgoingIsLoading = true;

      this.$api.integrations.setMailGunOutgoingEmailName(this.editUserDomainForm)
        .then(res => {
          this.$buefy.toast.open({
            message: "Outgoing email setup",
            type: "is-success"
          });

          return res;
        })
        .finally(() => {
          this.saveOutgoingIsLoading = false;
          this.editOutgoingEmailUserId = null;
        });
    },
    verifyEmail(user) {
      this.editUserDomainForm.verifyButtonLoading = true;
      const data = {
        user_id: user.id,
        integration_id: this.getMailgunIntegrationByUserId(user.id).id,
      };

      this.$api.integrations.verifyForwardingEmail(data)
        .then(res => {
          this.$buefy.toast.open({
            message: "Verifying email address has begun. At least 5 minutes to propagate.",
            type: "is-success",
            duration: 4500
          });
        })
        .finally(() => this.editUserDomainForm.verifyButtonLoading = false);
    },
    updateDomainProvider() {
      const data = {
        'domain_provider': (typeof this.domainProviderSelected === "number") ? this.domainProviderSelected : null
      };

      this.$api.accounts.updateDomainProvider(data)
        .then(res => {})
        .finally(() => {});
    },
    getDomainHostValue(value) {
      const domain = this.account.email_domain;
      const domainProvider = this.domainProviderHostValues.find(p => p.type === this.domainProviderSelected);

      // we don't have the domain provider DNS setup
      if (domainProvider === undefined) {
        return value;
      }

      // do nothing when domain should be included when setting up DNS
      if (domainProvider.config.includeDomain) {
        return value;
      }

      const newValue = value.replace(domain, '').trim();
      if (newValue === '' && domainProvider.config.domainSymbol) {
        return domainProvider.config.domainSymbol;
      }

      // remove . at end of string
      return newValue.replace(/\.$/, "");
    },
    copyText(text) {
      this.$utils.copyToClipboard(text);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

::v-deep .max-w-300 {
  max-width: 300px;
  word-wrap: break-word;
}

.has-text-muted {
  color: $text-muted;
}

.section_domain {
  display: inline-flex;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 1.125rem;
  line-height: 40px;

  strong {
    margin-right: 1rem;
  }

  &--link {
    margin-left: 1rem;
    color: $link;
    font-size: 0.875rem;
    cursor: pointer;

    &:hover {
      color: $link-hover;
    }
  }
}

.section_heading {
  margin: 2rem 0 0.5rem;
  font-size: 1.25rem;
  font-weight: bold;
  line-height: 1.3;
}

.section_description {
  max-width: 600px;
  margin-bottom: 1.5rem;
  line-height: 1.3;
}

.domain-provider {
  margin-bottom: 1rem;
}

.step {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid $border;

  &--heading {
    margin-bottom: 8px;
    font-size: 1.25rem;
    font-weight: bold;
  }

  &--description {
    margin-bottom: 8px;
    line-height: 1.3;
  }

  &--directions {
    margin-top: 2rem;
    line-height: 1.3;
  }

  &--link {
    margin-bottom: 4px;
  }
}

.table_link {
  margin-left: 0.5rem;
  font-size: 0.875rem;
  color: $link;
  cursor: pointer;

  &:hover {
    color: $link-hover;
  }

  strong {
    color: $link;

    &:hover {
      color: $link-hover;
    }
  }
}
</style>
